import type { DesignAsset, DesignShape } from '../../types/design'
import type { FormDefinition, FormField, FormResponse } from '../../types/forms'
import type { WorkspaceFileMetadata } from '../../types/schema'
import type { DataContext } from '../../types/aiChatSource'

const compact = (value: unknown) =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : ''

const limitText = (value: string, max = 240) =>
  value.length > max ? `${value.slice(0, max - 1).trimEnd()}...` : value

const fieldOptions = (field: FormField) => {
  if (field.type === 'PERSON') {
    return field.allowMultiple ? '(multiple members allowed)' : '(single member)'
  }
  if (field.type !== 'DROPDOWN') return ''
  const options = field.options.map((option) => compact(option.label || option.value)).filter(Boolean).slice(0, 6)
  return options.length ? ` Options: ${options.join(', ')}.` : ''
}

const summarizeFormField = (field: FormField, index: number) => {
  const required = field.required ? 'required' : 'optional'
  const description = compact(field.description)
  return [
    `${index + 1}. ${compact(field.label) || 'Untitled field'} (${field.type}, ${required}).`,
    description ? `Notes: ${limitText(description, 160)}.` : '',
    fieldOptions(field),
  ].filter(Boolean).join(' ')
}

export const formDefinitionToDataContext = (form: FormDefinition): DataContext => {
  const title = compact(form.title) || 'Untitled form'
  const description = compact(form.description)
  const fields = [...form.fields]
    .sort((left, right) => left.order - right.order)
    .slice(0, 24)
    .map(summarizeFormField)
  return {
    id: `workspace-form:${form.id}`,
    kind: 'form',
    label: `Form: ${title}`,
    formId: form.id,
    formResponseCount: form.submissionCount,
    text: [
      `[Workspace form context: ${title}]`,
      `Form ID: ${form.id}`,
      `Lifecycle: ${form.lifecycleStatus}`,
      `Submissions: ${form.submissionCount}`,
      description ? `Description: ${description}` : '',
      fields.length ? 'Fields:' : 'Fields: none listed.',
      ...fields,
      form.fields.length > fields.length ? `Additional fields omitted: ${form.fields.length - fields.length}` : '',
      '[End workspace form context]',
    ].filter(Boolean).join('\n'),
  }
}

export const formResponsesToDataContext = (
  form: FormDefinition,
  responses: FormResponse[],
): DataContext => {
  const title = compact(form.title) || 'Untitled form'
  const responseLines = responses.slice(0, 50).map((r, i) => {
    const submitter = compact(r.submittedByName) || compact(r.submittedBy) || 'Anonymous'
    const date = r.submittedAt ? (typeof r.submittedAt === 'object' && 'toDate' in r.submittedAt ? (r.submittedAt as any).toDate().toISOString() : String(r.submittedAt)) : 'Unknown date'
    const fieldSnippets = Object.entries(r.data || {})
      .slice(0, 3)
      .map(([k, v]) => `${k}: ${compact(String(v))}`)
      .join(' | ')
    return `Response ${i + 1} | Submitter: ${submitter} | Date: ${date} | Status: ${r.status}${fieldSnippets ? ` | Data: ${fieldSnippets}` : ''}`
  })
  return {
    id: `workspace-form-responses:${form.id}`,
    kind: 'form_responses',
    label: `Form: ${title} (${responses.length} response${responses.length === 1 ? '' : 's'})`,
    formId: form.id,
    formResponseIds: responses.map((r) => r.id),
    formResponseCount: responses.length,
    text: [
      `[Workspace form responses: ${title}]`,
      `Form ID: ${form.id}`,
      `Selected responses: ${responses.length}`,
      ...responseLines,
      responses.length > responseLines.length ? `Additional responses omitted: ${responses.length - responseLines.length}` : '',
      '[End workspace form responses]',
    ].filter(Boolean).join('\n'),
  }
}

const summarizeDesignShape = (shape: DesignShape, index: number) => {
  const label = compact(shape.text || shape.name)
  const role = shape.semanticRole ? ` role=${shape.semanticRole}` : ''
  const binding = shape.binding?.linkedNodeId ? ` linkedNode=${shape.binding.linkedNodeId}` : ''
  return `${index + 1}. ${shape.type}${role}${binding}${label ? `: ${limitText(label, 140)}` : ''}`
}

export const designAssetToDataContext = (asset: DesignAsset): DataContext => {
  const title = compact(asset.title) || 'Untitled design'
  const shapes = asset.shapes.slice(0, 30).map(summarizeDesignShape)
  return {
    id: `workspace-design:${asset.id}`,
    kind: 'design',
    label: `Design: ${title}`,
    text: [
      `[Workspace design context: ${title}]`,
      `Design ID: ${asset.id}`,
      `Mode: ${asset.mode}`,
      asset.boundIntent ? `Bound intent: ${asset.boundIntent}` : '',
      asset.templateOf ? `Template: ${asset.templateOf}` : '',
      `Shape count: ${asset.shapes.length}`,
      shapes.length ? 'Shapes:' : 'Shapes: none listed.',
      ...shapes,
      asset.shapes.length > shapes.length ? `Additional shapes omitted: ${asset.shapes.length - shapes.length}` : '',
      '[End workspace design context]',
    ].filter(Boolean).join('\n'),
  }
}

export const workspaceFileToDataContext = (file: WorkspaceFileMetadata): DataContext => {
  const label = compact(file.fileName) || 'Untitled upload'
  const source = compact(file.source)
  const origin = compact(file.origin)
  return {
    id: `asset:${file.id}`,
    kind: file.artifactType === 'AI_MAGIC_UPLOAD' ? 'upload' : 'asset',
    label,
    sizeBytes: file.sizeBytes,
    text: [
      `[Workspace upload context: ${label}]`,
      `File ID: ${file.id}`,
      `Artifact type: ${file.artifactType}`,
      file.artifactId ? `Artifact ID: ${file.artifactId}` : '',
      file.documentType ? `Document type: ${file.documentType}` : '',
      file.mimeType ? `MIME type: ${file.mimeType}` : '',
      source ? `Source: ${source}` : '',
      origin ? `Origin: ${origin}` : '',
      `Visibility: ${file.visibility}`,
      'Only file metadata is attached here unless the file was uploaded directly in this chat.',
      '[End workspace upload context]',
    ].filter(Boolean).join('\n'),
    fileId: file.id,
    mimeType: file.mimeType,
  }
}
